import { useBrand } from '@contexts/index'
import type { ReactNode } from 'react'

type Props = {
  stepNumber: number
  label: string
  currentStep: number
  isCompleted: boolean
  isExpanded: boolean
  summaryText?: string
  onChangeClick: () => void
  onExpandToggle: () => void
  expandedContent?: ReactNode
  trackingName?: string
}

const ChevronUp = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={3}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 15.75l7.5-7.5 7.5 7.5"
    />
  </svg>
)

const ChevronDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={3}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
)

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
)

const StepIndicator = ({
  stepNumber,
  label,
  currentStep,
  isCompleted,
  isExpanded,
  summaryText,
  onChangeClick,
  onExpandToggle,
  expandedContent,
  trackingName = 'form',
}: Props) => {
  const brand = useBrand()
  const isActive = currentStep === stepNumber
  const brandClass = brand === 'lexus' ? ' uppercase' : ''

  // Pending state - step not yet completed
  if (!isCompleted) {
    return (
      <div className="flex flex-row">
        <div className={`${isActive ? 'font-semibold' : 'font-medium'}`}>
          {stepNumber}. {label}
        </div>
      </div>
    )
  }

  // Completed state - show summary with change option
  return (
    <div
      className={`relative${brandClass}`}
      data-gt-name="componentevent"
      data-gt-componentname={trackingName}
    >
      <div className="md:flex items-center">
        <button className="flex hover:underline" onClick={onChangeClick}>
          <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
            <div className={`font-medium${brandClass}`}>
              {stepNumber}. {label}
            </div>
            <span className="text-tiny">Change</span>
          </div>
        </button>
      </div>

      {/* Summary text with expand toggle */}
      {summaryText && (
        <button
          className="md:flex text-tiny hidden mt-1"
          data-gt-action={`nav-${isExpanded ? 'close' : 'open'}-dropdown`}
          data-gt-label={label.toLowerCase()}
          onClick={onExpandToggle}
        >
          <div className="break-all w-full">{summaryText}</div>
          <div className="ml-1 mt-1">
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </div>
        </button>
      )}

      {/* Expanded dropdown content */}
      {isExpanded && expandedContent && (
        <div
          className="absolute top-[70px] p-[15px] shadow-md z-10 w-[300px] bg-white"
          data-gt-componentgroup="forms"
          data-gt-name="componentevent"
          data-gt-componentname={trackingName}
        >
          <div className="flex justify-between">
            <div className="grow">{expandedContent}</div>
            <button
              onClick={onExpandToggle}
              data-gt-action="nav-close-dropdown"
              data-gt-label={label.toLowerCase()}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default StepIndicator
